import { ChevronLeft } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-rdc-cream">
      {/* Header */}
      <div className="bg-white border-b border-rdc-stone/30">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-rdc-taupe hover:text-rdc-charcoal transition-colors mb-6"
          >
            <ChevronLeft size={18} />
            <span className="text-sm font-medium">Back</span>
          </Link>
          <h1 className="page-title">Terms of Service</h1>
          <p className="text-rdc-taupe mt-2">
            Last updated: February 16, 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-sm max-w-none text-rdc-charcoal">
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">Acceptance of Terms</h2>
            <p className="text-base leading-relaxed mb-4">
              These Terms of Service ("Terms", "Terms of Service") govern your access to and use of the Riding & Driving Club ("RDC") Digital Garage platform, including all content, functionality, and services offered on or through ridinganddrivingclub.com (the "Service").
            </p>
            <p className="text-base leading-relaxed mb-4">
              Please read these Terms carefully before using the Service. By accessing or using RDC Digital Garage in any manner, you acknowledge that you have read, understood, and agree to be bound by all the terms and conditions contained herein. If you do not agree with any part of these Terms, you may not use the Service.
            </p>
            <p className="text-base leading-relaxed">
              Riding & Driving Club reserves the right to modify these Terms at any time. Your continued use of the Service following the posting of revised Terms means that you accept and agree to the changes.
            </p>
          </section>

          {/* 1. Description of Service */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">1. Description of Service</h2>
            <p className="text-base leading-relaxed mb-4">
              The RDC Digital Garage platform provides a comprehensive management solution for car collectors, including:
            </p>
            <ul className="list-disc list-inside space-y-3 text-base text-rdc-charcoal">
              <li><strong>Digital Garage Management:</strong> Track, organize, and manage your vehicle collection with detailed specifications, photos, and documentation</li>
              <li><strong>Service & Maintenance Tracking:</strong> Record and monitor service history, maintenance schedules, and vehicle alerts</li>
              <li><strong>AI Concierge Service:</strong> AI-powered assistance for vehicle inquiries, appointment scheduling, and service coordination</li>
              <li><strong>Service Provider Directory:</strong> Access to vetted specialists and service providers for vehicle maintenance and care</li>
              <li><strong>Club Directory:</strong> Connect with fellow club members and participate in community features</li>
              <li><strong>Vehicle Valuation Tools:</strong> Track vehicle appraisals and valuation history</li>
              <li><strong>Appointment & Work Order Management:</strong> Schedule and track service appointments and work orders</li>
              <li><strong>Document Management:</strong> Upload and organize vehicle-related documents and records</li>
            </ul>
          </section>

          {/* 2. Account Registration and Responsibilities */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">2. Account Registration and Responsibilities</h2>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Account Creation</h3>
              <p className="text-base leading-relaxed">
                To access certain features of the Service, you must create an account and provide accurate, complete, and current information. You are responsible for maintaining the confidentiality of your password and for all activities that occur under your account. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base text-rdc-charcoal mt-3">
                <li>Provide accurate and complete information during registration</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Eligibility</h3>
              <p className="text-base leading-relaxed">
                You represent and warrant that you are at least 18 years of age and have the legal capacity to enter into these Terms. If you are under 18, you must obtain parental or guardian consent.
              </p>
            </div>
          </section>

          {/* 3. SMS Communications Consent */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">3. SMS Communications Consent</h2>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Consent to SMS Messages</h3>
              <p className="text-base leading-relaxed mb-3">
                By providing your phone number during account setup or in your settings, you explicitly consent to receive SMS (text) messages from Riding & Driving Club. These messages include:
              </p>
              <ul className="list-disc list-inside space-y-3 text-base text-rdc-charcoal">
                <li><strong>Transactional Messages:</strong> Work order status updates, appointment confirmations, and critical service notifications that are essential to your service experience</li>
                <li><strong>Informational Messages:</strong> Service reminders, maintenance alerts, vehicle status notifications, and scheduled appointment reminders</li>
                <li><strong>Concierge Messages:</strong> Updates and responses from our AI concierge service regarding your requests and vehicle inquiries</li>
                <li><strong>Account Messages:</strong> Important account notifications including security alerts and account updates</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Message Rates and Frequency</h3>
              <p className="text-base leading-relaxed mb-3">
                Standard message and data rates apply according to your mobile service provider's plan. Message frequency varies based on your activity level and preferences. You can expect typically 1-5 messages per month, but this may vary based on:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base text-rdc-charcoal">
                <li>Pending work orders and appointments</li>
                <li>Scheduled maintenance reminders</li>
                <li>Concierge requests and responses</li>
                <li>Your chosen notification preferences</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Opt-Out Rights</h3>
              <p className="text-base leading-relaxed mb-3">
                You can opt out of SMS communications at any time by:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base text-rdc-charcoal mb-3">
                <li>Replying "STOP" to any text message from RDC</li>
                <li>Modifying your notification preferences in your account settings</li>
                <li>Contacting support at michael@ridinganddrivingclub.com</li>
              </ul>
              <p className="text-base leading-relaxed text-rdc-burgundy font-medium">
                Important: Opting out of SMS messages may prevent you from receiving important service notifications and may limit functionality of certain features that depend on text message delivery.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Support and Help</h3>
              <p className="text-base leading-relaxed">
                If you have questions about our SMS communications or need technical support, reply "HELP" to any message or contact michael@ridinganddrivingclub.com for assistance.
              </p>
            </div>
          </section>

          {/* 4. AI Concierge Service */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">4. AI Concierge Service Disclaimer</h2>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">AI-Assisted Service</h3>
              <p className="text-base leading-relaxed mb-4">
                Our concierge service is powered by artificial intelligence (AI) technology provided by Anthropic. The service provides assistance with vehicle-related inquiries, appointment scheduling, and service coordination. You acknowledge and agree that:
              </p>
              <ul className="list-disc list-inside space-y-3 text-base text-rdc-charcoal">
                <li><strong>Not Professional Advice:</strong> The AI concierge service is not a substitute for professional mechanical advice, legal counsel, or expert automotive services. Always consult qualified professionals for serious vehicle issues</li>
                <li><strong>Calls on Your Behalf:</strong> The concierge service may make phone calls to service providers and specialists on your behalf to coordinate appointments and services. You authorize RDC to make such calls using information you provide</li>
                <li><strong>Limitations:</strong> The AI service provides general information and coordination services but cannot replace professional expertise or personalized professional consultation</li>
                <li><strong>No Guarantees:</strong> RDC makes no guarantee regarding the accuracy, completeness, or suitability of information or recommendations provided by the AI concierge service</li>
                <li><strong>Your Responsibility:</strong> You remain responsible for verifying information and making final decisions regarding your vehicles and services</li>
              </ul>
            </div>
          </section>

          {/* 5. Intellectual Property */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">5. Intellectual Property Rights</h2>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">RDC Content and Platform</h3>
              <p className="text-base leading-relaxed mb-4">
                All content, features, and functionality of the Service, including but not limited to text, graphics, logos, images, and software, are the exclusive property of Riding & Driving Club or its content suppliers and are protected by international copyright, trademark, and other intellectual property laws.
              </p>
              <p className="text-base leading-relaxed">
                You are granted a limited, non-exclusive, non-transferable license to access and use the Service for your personal, non-commercial use. You may not reproduce, distribute, transmit, display, perform, publish, license, create derivative works from, transfer, or sell any information, software, products, or services obtained from the Service.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-rdc-black mb-3">Your Content</h3>
              <p className="text-base leading-relaxed">
                You retain ownership of any content you upload to the Service (such as vehicle photos, documents, and descriptions). By uploading content, you grant RDC a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display your content solely for the purpose of providing the Service and improving our platform.
              </p>
            </div>
          </section>

          {/* 6. Limitation of Liability */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">6. Limitation of Liability</h2>
            <p className="text-base leading-relaxed mb-4">
              TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT SHALL RIDING & DRIVING CLUB, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
            </p>
            <ul className="list-disc list-inside space-y-2 text-base text-rdc-charcoal mb-4">
              <li>Your use of or inability to use the Service</li>
              <li>Any unauthorized access to or alteration of your transmissions or data</li>
              <li>Any third-party conduct or content</li>
              <li>Any other matter relating to the Service</li>
            </ul>
            <p className="text-base leading-relaxed">
              The liability of Riding & Driving Club shall not exceed the amount you paid for the Service in the past 12 months. Some jurisdictions do not allow limitations on liability, so this limitation may not apply to you.
            </p>
          </section>

          {/* 7. Indemnification */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">7. Indemnification</h2>
            <p className="text-base leading-relaxed">
              You agree to indemnify and hold harmless Riding & Driving Club, its officers, directors, employees, and agents from and against any and all claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising out of or relating to: (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any rights of a third party; or (d) your content or information you provide.
            </p>
          </section>

          {/* 8. Termination */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">8. Termination</h2>
            <p className="text-base leading-relaxed mb-4">
              Riding & Driving Club may terminate or suspend your account and access to the Service immediately, without notice or liability, for any reason or no reason, including if you breach any provision of these Terms.
            </p>
            <p className="text-base leading-relaxed">
              Upon termination, your right to use the Service will cease immediately. You may request data deletion subject to legal retention requirements and applicable backup policies.
            </p>
          </section>

          {/* 9. Governing Law */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">9. Governing Law and Jurisdiction</h2>
            <p className="text-base leading-relaxed mb-4">
              These Terms of Service and your use of the Service shall be governed by and construed in accordance with the laws of the State of New York, United States, without regard to its conflict of law principles.
            </p>
            <p className="text-base leading-relaxed">
              You agree that any legal action or proceeding arising out of or relating to these Terms or the Service shall be brought exclusively in the state and federal courts located in New York County, New York, and you hereby irrevocably consent to the personal jurisdiction and venue of these courts.
            </p>
          </section>

          {/* 10. Disclaimer of Warranties */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">10. Disclaimer of Warranties</h2>
            <p className="text-base leading-relaxed mb-3">
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. RIDING & DRIVING CLUB DISCLAIMS ALL WARRANTIES, EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="text-base leading-relaxed">
              RDC does not warrant that: (a) the Service will meet your requirements; (b) the Service will be uninterrupted, timely, secure, or error-free; (c) any defects or errors in the Service will be corrected; or (d) the Service is free from viruses or other harmful components.
            </p>
          </section>

          {/* 11. Modifications to Service */}
          <section className="mb-8">
            <h2 className="section-title mb-4 text-rdc-black">11. Modifications to Service</h2>
            <p className="text-base leading-relaxed">
              Riding & Driving Club reserves the right to modify or discontinue, temporarily or permanently, the Service (or any part thereof) with or without notice to you. You agree that RDC shall not be liable to you or any third party for any modification, suspension, or discontinuation of the Service.
            </p>
          </section>

          {/* 12. Contact Information */}
          <section className="mb-12">
            <h2 className="section-title mb-4 text-rdc-black">12. Contact Information</h2>
            <p className="text-base leading-relaxed mb-4">
              If you have any questions about these Terms of Service or our practices, please contact us:
            </p>
            <div className="bg-rdc-cream rounded-lg p-6 border border-rdc-stone/30">
              <p className="text-base"><strong>Riding & Driving Club</strong></p>
              <p className="text-base text-rdc-charcoal mt-2">
                Email: michael@ridinganddrivingclub.com
              </p>
              <p className="text-base text-rdc-charcoal mt-2">
                Website: ridinganddrivingclub.com
              </p>
              <p className="text-sm text-rdc-taupe mt-4">
                We will respond to all inquiries within 30 days of receipt.
              </p>
            </div>
          </section>

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t border-rdc-stone/30 flex gap-6 justify-center text-sm">
            <Link to="/privacy" className="text-rdc-primary hover:underline">
              Privacy Policy
            </Link>
            <span className="text-rdc-stone">/</span>
            <Link to="/sms-consent" className="text-rdc-primary hover:underline">
              SMS Consent
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
